import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, Divider, Paper, Stack,
  Autocomplete, IconButton, Alert, CircularProgress, InputAdornment, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createRecipient, updateRecipient, fetchRecipientsByStudent, deleteRecipient } from '../slices/recipientSlice';
import { fetchSchoolYears } from '../slices/schoolYearsSlice';
import { Checkbox, FormControlLabel } from '@mui/material';
import { fetchStudents } from '../slices/studentsSlice';
import { useToast } from '../context/ToastContext';
import { fetchStudentFees, saveStudentFees, clearStudentFees, createFeeCategory } from '../slices/feesManagementSlice';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { fetchSchoolFees } from '../slices/schoolFeesSlice';
import { motion } from 'framer-motion';

import { paymentTypes } from '../constants/paymentTypes';
import { exportToExcel, exportToPrintWindow } from '../utils/exportUtils';
import BankBranchSelect from '../components/BankBranchSelect';

import { formatPhoneForWhatsApp } from '../utils/phoneUtils';

const MotionPaper = motion(Paper);

const StudentPaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const student = location.state?.student;
  const [selectedStudent, setSelectedStudent] = useState(student || null);
  const activeYearId = useSelector(state => state.schoolYears?.list?.filter(year => year.is_active)[0]?.id || null);
  const defaultFee = useSelector((state) => state.schoolFees?.data);
  const dispatch = useDispatch();
  const studentOptions = useSelector((state) =>
    Array.isArray(state.students?.students) ? state.students?.students : []
  );
  const { studentFees: resolvedFees, loading: feesLoading, saving: feesSaving } = useSelector((s) => s.feesManagement);
  const [feeOverrides, setFeeOverrides] = useState({});
  const [feeEditMode, setFeeEditMode] = useState(false);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [categoryNameInput, setCategoryNameInput] = useState('');
  const [categoryNameError, setCategoryNameError] = useState('');

  // Legacy fee inputs kept for fallback (accounts with no dynamic categories)
  const [feeInputs, setFeeInputs] = useState({
    annualFee: 0,
    uniformFee: 0,
    transportFee: 0,
    booksFee: 0,
    clothesFeePaid: false,
    previousDebt: 0,
  });

  const getPaymentTypeId = (displayName) => {
    return paymentTypes.find(pt => pt.display_name === displayName)?.id || null;
  };

   const validatePayments = () => {
  const errors = {};
  let totalPaidAmount = 0;

  payments.forEach((payment, index) => {
    const paymentAmount = Number(payment.amount || 0);
    totalPaidAmount += paymentAmount;

    // Validate individual payment
    if (payment.amount && paymentAmount <= 0) {
      errors[`payment_${index}_amount`] = 'المبلغ يجب أن يكون أكبر من صفر';
    }

    // Check if current payment would exceed total fees
    if (totalPaidAmount > totalSchoolCost) {
      errors[`payment_${index}_amount`] = 'المبلغ المدفوع يتجاوز إجمالي الرسوم المدرسية';
    }

    // Validate required fields for non-empty payments
    if (payment.amount || payment.type || payment.date) {
      if (!payment.amount) {
        errors[`payment_${index}_amount`] = 'المبلغ مطلوب';
      }
      if (!payment.type) {
        errors[`payment_${index}_type`] = 'طريقة الدفع مطلوبة';
      }
      if (!payment.date) {
        errors[`payment_${index}_date`] = 'تاريخ الدفع مطلوب';
      }
    }

    // Additional validation for cheques
    if (payment.type === 'شيك') {
      if (!payment.chequeDueDate) {
        errors[`payment_${index}_chequeDueDate`] = 'تاريخ استحقاق الشيك مطلوب';
      }
      if (!payment.chequeNumber) {
        errors[`payment_${index}_chequeNumber`] = 'رقم الشيك مطلوب';
      }
    }
  });

  // Global validation
  if (totalPaidAmount > totalSchoolCost) {
    errors.global = `إجمالي المبلغ المدفوع (${totalPaidAmount} شيكل) يتجاوز إجمالي الرسوم المدرسية (${totalSchoolCost} شيكل)`;
  }

  setValidationErrors(errors);
  return Object.keys(errors).length === 0;
};

  useEffect(() => {
    if (!student) {
      dispatch(fetchStudents());
    }
    dispatch(fetchSchoolFees());
    dispatch(fetchSchoolYears());
  }, [dispatch, student]);

  // Load dynamic fees when student + year are available
  useEffect(() => {
    if (selectedStudent?.id && activeYearId) {
      dispatch(clearStudentFees());
      dispatch(fetchStudentFees({ studentId: selectedStudent.id, schoolYearId: activeYearId }));
      setFeeOverrides({});
      setFeeEditMode(false);
    }
  }, [dispatch, selectedStudent?.id, activeYearId]);

  const openAddCategoryDialog = () => { setCategoryNameInput(''); setCategoryNameError(''); setAddCategoryOpen(true); };
  const closeAddCategoryDialog = () => setAddCategoryOpen(false);

  const handleSaveCategory = async () => {
    if (!categoryNameInput.trim()) { setCategoryNameError('اسم الرسوم مطلوب'); return; }
    await dispatch(createFeeCategory({ name: categoryNameInput.trim() }));
    closeAddCategoryDialog();
    if (selectedStudent?.id && activeYearId) {
      dispatch(fetchStudentFees({ studentId: selectedStudent.id, schoolYearId: activeYearId }));
    }
  };

  // Legacy fallback fee loading
  useEffect(() => {
    const studentFee = selectedStudent?.school_fees;
    if (studentFee && !resolvedFees) {
      setFeeInputs({
        annualFee: parseFloat(studentFee.school_fee || 0),
        booksFee: parseFloat(studentFee.books_fee || 0),
        transportFee: parseFloat(studentFee.trans_fee || 0),
        uniformFee: parseFloat(studentFee.clothes_fee || 0),
        clothesFeePaid: studentFee.clothes_fee_paid || false,
        previousDebt: parseFloat(studentFee.previous_debt || 0),
      });
    } else if (defaultFee && !resolvedFees) {
      setFeeInputs({
        annualFee: parseFloat(defaultFee.school_fee || 0),
        booksFee: parseFloat(defaultFee.books_fee || 0),
        transportFee: parseFloat(defaultFee.trans_fee || 0),
        uniformFee: parseFloat(defaultFee.clothes_fee || 0),
        clothesFeePaid: defaultFee.clothes_fee_paid || false,
        previousDebt: 0,
      });
    }
  }, [selectedStudent, defaultFee, resolvedFees]);

  // Whether this student actually has any non-zero legacy fee amount configured —
  // checked on the resolved amounts, not just presence of a (possibly all-zero) fee record.
  const hasLegacyFeeData = (
    Number(feeInputs.annualFee || 0) > 0 ||
    Number(feeInputs.transportFee || 0) > 0 ||
    Number(feeInputs.booksFee || 0) > 0 ||
    Number(feeInputs.uniformFee || 0) > 0 ||
    Number(feeInputs.previousDebt || 0) > 0
  );

  // Use dynamic total if available, else legacy 4-field total
  const totalSchoolCost = useMemo(() => {
    if (resolvedFees?.fee_items?.length > 0) {
      // previous_debt is carried-forward debt, not an editable/discountable fee category -
      // it's returned separately by the backend and always added on top of the fee items.
      const previousDebt = Number(resolvedFees.previous_debt || 0);
      // Apply any in-progress overrides to the total
      if (feeEditMode) {
        const itemsTotal = resolvedFees.fee_items.reduce((sum, item) => {
          const override = feeOverrides[item.category_id];
          const amount = override !== undefined && override !== '' ? parseFloat(override) : item.final_amount;
          return sum + (amount || 0);
        }, 0);
        return itemsTotal + previousDebt;
      }
      const dynamicTotal = resolvedFees.fee_items.reduce((sum, item) => sum + (item.final_amount || 0), 0);
      return dynamicTotal + previousDebt;
    }
    return (
      Number(feeInputs.annualFee || 0) +
      Number(feeInputs.uniformFee || 0) +
      Number(feeInputs.transportFee || 0) +
      Number(feeInputs.booksFee || 0) +
      Number(feeInputs.previousDebt || 0)
    );
  }, [resolvedFees, feeInputs, feeOverrides, feeEditMode]);

  const [payments, setPayments] = useState([
    {
      amount: '',
      type: '',
      paymentTypeId: '',
      date: '',
      chequeDate: '',
      chequeDueDate: '',
      chequeImage: null,
      chequeImagePreview: '',
      note: '',
      description: '', // ✅ Added dedicated description field
      delivered: false,
      received: false,
      isExisting: false,        // ✅ ADD THIS
      receivedFromAPI: false, 
      missingDetails: '',
      created_by_name: '' ,
      visaReceiptNumber: '',
    }
  ]);

  const handleSaveFee = async () => {
    if (!selectedStudent || !activeYearId) return;
    setIsSaving(true);
    try {
      if (resolvedFees?.fee_items?.length > 0) {
        // Dynamic system: save per-category overrides
        const items = resolvedFees.fee_items.map((item) => {
          const override = feeOverrides[item.category_id];
          if (override === undefined || override === '') {
            return { category_id: item.category_id, remove: true };
          }
          return { category_id: item.category_id, amount: parseFloat(override) };
        });
        await dispatch(saveStudentFees({
          studentId: selectedStudent.id,
          schoolYearId: activeYearId,
          items,
        })).unwrap();
        // Refresh resolved fees
        await dispatch(fetchStudentFees({ studentId: selectedStudent.id, schoolYearId: activeYearId }));
        setFeeEditMode(false);
      }
      showToast('تم حفظ الرسوم بنجاح', 'success');
    } catch (error) {
      console.error('❌ Failed to save fee:', error);
      showToast('فشل في حفظ الرسوم', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFeeChange = (field, value) => {
    setFeeInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleChange = (index, field, value) => {
  const updated = [...payments];
  updated[index][field] = value;
  setPayments(updated);

  // Clear specific field error when user starts typing
  const errorKey = `payment_${index}_${field}`;
  if (validationErrors[errorKey]) {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[errorKey];
      return newErrors;
    });
  }

  // Real-time validation for amount field
  if (field === 'amount') {
    setTimeout(() => {
      const newTotalPaid = updated.reduce((sum, p) => sum + Number(p.amount || 0), 0);
      if (newTotalPaid > totalSchoolCost) {
        setValidationErrors(prev => ({
          ...prev,
          [`payment_${index}_amount`]: 'المبلغ المدفوع يتجاوز إجمالي الرسوم المدرسية',
          global: `إجمالي المبلغ المدفوع (${newTotalPaid} شيكل) يتجاوز إجمالي الرسوم المدرسية (${totalSchoolCost} شيكل)`
        }));
      } else {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[`payment_${index}_amount`];
          delete newErrors.global;
          return newErrors;
        });
      }
    }, 300); // Debounce validation
  }
};

  const handleFileChange = (index, file) => {
    const updated = [...payments];
    updated[index].chequeImage = file;
    updated[index].chequeImagePreview = URL.createObjectURL(file);
    setPayments(updated);
  };

  const handleRemoveImage = (index) => {
    const updated = [...payments];
    if (updated[index].chequeImagePreview && updated[index].chequeImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(updated[index].chequeImagePreview);
    }
    updated[index].chequeImage = null;
    updated[index].chequeImagePreview = '';
    setPayments(updated);
  };

  const handleViewImage = (imageUrl) => {
    window.open(imageUrl, '_blank');
  };

  const addPaymentRow = () => {
    setPayments([
      ...payments,
      {
        amount: '',
        type: '',
        date: '',
        chequeDate: '',
        chequeDueDate: '',
        paymentTypeId: '',
        chequeImage: null,
        chequeImagePreview: '',
        received: false,         // ✅ ADD THIS
        isExisting: false,       // ✅ ADD THIS
        receivedFromAPI: false, 
        note: '',
        description: '', // ✅ Added description for new rows
        delivered: false,
        missingDetails: '',
        visaReceiptNumber: '',
        created_by_name: '' // ✅ Added creator name for new rows
      }
    ]);
  };

  const handleDeletePaymentClick = (index) => {
    setPaymentToDelete(index);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeletePayment = () => {
    const payment = payments[paymentToDelete];
    setDeleteDialogOpen(false);
    if (payment.isExisting && payment.id) {
      dispatch(deleteRecipient(payment.id))
        .unwrap()
        .then(() => {
          setPayments((prev) => prev.filter((_, i) => i !== paymentToDelete));
          showToast('تم حذف الدفعة بنجاح', 'success');
        })
        .catch(() => {
          showToast('فشل حذف الدفعة', 'error');
        });
    } else {
      setPayments((prev) => prev.filter((_, i) => i !== paymentToDelete));
    }
    setPaymentToDelete(null);
  };

  const handleCancelDeletePayment = () => {
    setDeleteDialogOpen(false);
    setPaymentToDelete(null);
  };

useEffect(() => {
  if (selectedStudent?.id) {
    dispatch(fetchRecipientsByStudent(selectedStudent.id)).then(({ payload }) => {
      const mapped = payload.map((r) => ({
        amount: r.amount,
        type: r.payment_type,
        paymentTypeId: getPaymentTypeId(r.payment_type),
        date: r.date,
        note: r.note || '', // General notes
        description: r.cheque?.description || r.visa?.description || '',
        delivered: false,
        received: r.received,           // ✅ CHANGE THIS
        receivedFromAPI: r.received,
        id: r.id,
        isExisting: true,
        bankNumber: r.cheque?.bank_number || '',
        branchNumber: r.cheque?.branch_number || '',
        bankName: r.cheque?.bank_name || '',
        branchName: r.cheque?.branch_name || '',
        countryCode: r.cheque?.country_code || '',
        // Reference ids aren't persisted (only name/number snapshots), so on
        // edit the select starts unresolved — user can re-pick from the list
        // or keep the existing name/number as-is (same pattern as EditPaymentPage.js).
        bankId: null,
        branchId: null,
        accountNumber: r.cheque?.account_number || '',
        chequeNumber: r.cheque?.cheque_number || '',
        chequeDueDate: r.cheque?.cheque_date || '',
        chequeImagePreview: r.cheque?.cheque_image || '',
        visaReceiptNumber: r.visa?.receipt_number || '',
        created_by_name: r.created_by_name || '', // ✅ Added creator name from API
      }));
      setPayments(mapped);
    });
  }
}, [dispatch, selectedStudent]);

  const handleFinish = async () => {
    // Validate before saving
    if (!validatePayments()) {
      showToast('يرجى تصحيح الأخطاء قبل الحفظ', 'error');
      return;
    }

    if (!selectedStudent) {
      showToast('يرجى اختيار طالب', 'error');
      return;
    }

    setIsSaving(true);
    try {
      // ... existing save logic
      for (const p of payments) {
        if (!p.amount || !p.type || !p.date) continue;
        

        const payload = {
          student_id: selectedStudent?.id || '',
          school_fee: p.feeId || '',
          payment_type: p.type || '',
          school_year: activeYearId,
          amount: Number(p.amount),
          date: p.date,
          received: p.received,
          note: p.note || '',
          bankNumber: p.bankNumber || '',
          branchNumber: p.branchNumber || '',
          bankName: p.bankName || '',
          branchName: p.branchName || '',
          countryCode: p.countryCode || '',
          accountNumber: p.accountNumber || '',
          chequeNumber: p.chequeNumber || '',
          chequeDueDate: p.chequeDueDate || '',
          chequeImage: p.chequeImage || '',
          description: p.description || '',
          visaReceiptNumber: p.visaReceiptNumber || '',
        };

        let updatedPayment;
        if (p.isExisting && p.id) {
          updatedPayment = await dispatch(updateRecipient({ id: p.id, ...payload })).unwrap();
        } else {
          updatedPayment = await dispatch(createRecipient(payload)).unwrap();
        }

        setPayments(prev => prev.map(payment => 
          payment === p ? {
            ...payment,
            received: updatedPayment.received,
            receivedFromAPI: updatedPayment.received,
            id: updatedPayment.id
          } : payment
        ));
      }

      showToast('تم حفظ الدفعات بنجاح', 'success');
      navigate('/recipient');
    } catch (error) {
      console.error('❌ Failed:', error);
      showToast('حدث خطأ أثناء حفظ الدفعة', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Print/export helpers
  const handleExportExcel = () => {
    const columns = [
      { header: 'رقم الهوية', key: 'student_id' },
      { header: 'اسم الطالب', key: 'student_name' },
      { header: 'اسم ولي الأمر', key: 'parent_name' },
      { header: 'الهاتف', key: 'parent_phone' },
      { header: 'الصف', key: 'class_name' },
      { header: 'المبلغ', key: 'amount' },
      { header: 'طريقة الدفع', key: 'payment_type' },
      { header: 'تاريخ الدفع', key: 'date' },
      { header: 'ملاحظات', key: 'note' },
      { header: 'رقم إيصال الفيزا', key: 'visa_receipt' },
      { header: 'تم التحصيل', key: 'received' },
      { header: 'منشئ السند', key: 'created_by_name' }, // ✅ Added creator column
    ];
    const data = payments.map((p) => ({
      student_id: selectedStudent?.student_id || '',
      student_name: `${selectedStudent?.first_name || ''} ${selectedStudent?.second_name || ''}`,
      parent_name: selectedStudent?.parent_name || '',
      parent_phone: selectedStudent?.parent_phone || '',
      class_name: selectedStudent?.school_class_name || '',
      amount: p.amount,
      payment_type: p.type,
      date: p.date,
      note: p.note,
      visa_receipt: p.visaReceiptNumber || '-',
      received: p.received ? 'نعم' : 'لا',
      created_by_name: p.created_by_name || '-', // ✅ Added creator data
    }));
    exportToExcel(data, columns, 'student_payment');
  };

    const totalPaid = useMemo(() => {
  return payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
}, [payments]);

const totalCollected = useMemo(() => {
  return payments.reduce((sum, p) => sum + (p.received ? Number(p.amount || 0) : 0), 0);
}, [payments]);

const totalUncollected = totalPaid - totalCollected;

const remainingAmount = useMemo(() => {
  return Math.max(totalSchoolCost - totalPaid, 0);
}, [totalSchoolCost, totalPaid]);

const isOverpaid = useMemo(() => {
  return totalPaid > totalSchoolCost;
}, [totalPaid, totalSchoolCost]);

  const handlePrint = async () => {
    const columns = [
      { header: 'رقم الهوية', key: 'student_id' },
      { header: 'اسم الطالب', key: 'student_name' },
      { header: 'اسم ولي الأمر', key: 'parent_name' },
      { header: 'الهاتف', key: 'parent_phone' },
      { header: 'الصف', key: 'class_name' },
      { header: 'المبلغ', key: 'amount' },
      { header: 'طريقة الدفع', key: 'payment_type' },
      { header: 'تاريخ الدفع', key: 'date' },
      { header: 'ملاحظات', key: 'note' },
      { header: 'تم التحصيل', key: 'received' },
      { header: 'رقم إيصال الفيزا', key: 'visa_receipt' },
      { header: 'منشئ السند', key: 'created_by_name' }, // ✅ Added creator column
    ];
    const data = payments.map((p) => ({
      student_id: selectedStudent?.student_id || '',
      student_name: `${selectedStudent?.first_name || ''} ${selectedStudent?.second_name || ''}`,
      parent_name: selectedStudent?.parent_name || '',
      parent_phone: selectedStudent?.parent_phone || '',
      class_name: selectedStudent?.school_class_name || '',
      amount: p.amount,
      payment_type: p.type,
      date: p.date,
      note: p.note,
      visa_receipt: p.visaReceiptNumber || '-',
      received: p.received ? 'نعم' : 'لا',
      created_by_name: p.created_by_name || '-', // ✅ Added creator data
    }));
    await exportToPrintWindow(data, columns, 'سندات دفع الطالب');
  };

  return (
    // 🎨 Design System Applied
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <Box
        sx={{
          direction: 'rtl',
          fontFamily: 'Cairo, Tajawal, sans-serif',
          p: { xs: 2, md: 3 },
          bgcolor: '#F8F9FF',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CreditScoreIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                صفحة دفع رسوم الطالب
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                إدارة الرسوم والدفعات والتحصيل
              </Typography>
            </Box>
          </Stack>
        </MotionPaper>

        {/* Actions */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: '#1E1B4B',
              fontFamily: 'Cairo, Tajawal, sans-serif',
            }}
          >
            تصدير القائمة
          </Typography>
          <Stack useFlexGap
            direction="row"
            spacing={1.5}
            sx={{ flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' }, alignItems: 'center', gap: 1.5 }}
          >
            <Button
              variant="outlined"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#16A34A',
                color: '#16A34A',
                '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' },
              }}
            >
              تصدير Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handlePrint}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#4338CA',
                color: '#4338CA',
                '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
              }}
            >
              طباعة
            </Button>
          </Stack>
        </MotionPaper>

        {/* Student Selector */}
        {!selectedStudent && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
            }}
          >
            <Autocomplete
              options={studentOptions}
              getOptionLabel={(option) =>
                `${option.student_id || ''} - ${option.first_name || ''} ${option.second_name || ''}`
              }
              onChange={(event, value) => setSelectedStudent(value)}
              renderInput={(params) => (
                <TextField {...params} label="ابحث حسب الاسم أو رقم الهوية" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, fontFamily: 'Cairo, Tajawal, sans-serif' }} />
              )}
            />
          </Paper>
        )}

        {/* Student Info & Fees */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: '#EEF2FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AccountCircleIcon sx={{ color: '#4338CA', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
              بيانات الطالب
            </Typography>
          </Stack>
          
          {/* Student Info Fields */}
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                label="رقم الهوية"
                value={selectedStudent?.student_id || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="الاسم الكامل"
                value={`${selectedStudent?.first_name || ''} ${selectedStudent?.second_name || ''}`}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="اسم ولي الأمر"
                value={selectedStudent?.parent_name || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="هاتف ولي الأمر"
                value={selectedStudent?.parent_phone || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }}
              />
            </Grid>
          </Grid>

          {/* Fee Breakdown — Dynamic or Legacy */}
          <Box mt={2}>
            {feesLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {!feesLoading && resolvedFees?.fee_items?.length > 0 ? (
              /* ── Dynamic fee breakdown ── */
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    تفاصيل الرسوم
                  </Typography>
                  <Stack useFlexGap direction="row" spacing={1}>
                    {!feeEditMode ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const map = {};
                          resolvedFees.fee_items.forEach(item => {
                            map[item.category_id] = item.final_amount ?? item.amount;
                          });
                          setFeeOverrides(map);
                          setFeeEditMode(true);
                        }}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 700,
                          textTransform: 'none',
                          fontFamily: 'Cairo, Tajawal, sans-serif',
                          borderColor: '#4338CA',
                          color: '#4338CA',
                          '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
                        }}
                      >
                        تعديل رسوم الطالب
                      </Button>
                    ) : (
                      <>
                        <Button size="small" variant="outlined" color="error"
                          onClick={() => { setFeeEditMode(false); setFeeOverrides({}); }}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 700,
                            textTransform: 'none',
                            fontFamily: 'Cairo, Tajawal, sans-serif',
                            borderColor: '#DC2626',
                            color: '#DC2626',
                            '&:hover': { borderColor: '#DC2626', bgcolor: '#FEE2E2' },
                          }}>
                          إلغاء
                        </Button>
                        <Button size="small" variant="contained"
                          disabled={isSaving || feesSaving}
                          onClick={handleSaveFee}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 700,
                            textTransform: 'none',
                            fontFamily: 'Cairo, Tajawal, sans-serif',
                            bgcolor: '#4338CA',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                          }}>
                          {isSaving ? 'جاري الحفظ...' : 'حفظ'}
                        </Button>
                      </>
                    )}
                  </Stack>
                </Stack>

                <Box sx={{ borderRadius: 2, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                  {resolvedFees.fee_items.map((item, idx) => (
                    <Box
                      key={item.category_id}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 2, py: 1.2,
                        backgroundColor: idx % 2 === 0 ? '#F8F9FF' : '#fff',
                        borderBottom: idx < resolvedFees.fee_items.length - 1 ? '1px solid #E5E7EB' : 'none',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight={600} sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{item.category_name}</Typography>
                        {item.rule_scope && (
                          <Chip
                            size="small"
                            label={item.rule_scope === 'student' ? 'خاص' : item.rule_scope === 'class' ? 'صف' : 'افتراضي'}
                            color={item.rule_scope === 'student' ? 'primary' : item.rule_scope === 'class' ? 'info' : 'default'}
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>

                      {feeEditMode ? (
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: 10 }}
                          placeholder={`محسوب: ${item.final_amount}`}
                          value={feeOverrides[item.category_id] ?? ''}
                          onChange={(e) => setFeeOverrides(p => ({ ...p, [item.category_id]: e.target.value }))}
                          sx={{ width: 140 }}
                          InputProps={{ startAdornment: <InputAdornment position="start">₪</InputAdornment> }}
                        />
                      ) : (
                        <Box sx={{ textAlign: 'left' }}>
                          {item.total_discount > 0 && (
                            <Typography variant="caption" sx={{ color: '#DC2626', display: 'block', textDecoration: 'line-through', textAlign: 'left', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                              {item.amount} ₪
                            </Typography>
                          )}
                          <Typography fontWeight={700} sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{item.final_amount} ₪</Typography>
                        </Box>
                      )}
                    </Box>
                  ))}

                  {/* Carried-forward debt (not editable here - set via year transition) */}
                  {resolvedFees.previous_debt > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, bgcolor: '#FFFBEB' }}>
                      <Typography sx={{ color: '#92400E', fontFamily: 'Cairo, Tajawal, sans-serif' }}>دين سابق مرحّل</Typography>
                      <Typography fontWeight={700} sx={{ color: '#92400E', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{resolvedFees.previous_debt} ₪</Typography>
                    </Box>
                  )}

                  {/* Totals row */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1.5, bgcolor: '#EEF2FF', borderTop: '2px solid #E5E7EB' }}>
                    <Typography fontWeight={700} sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                      الإجمالي
                      {resolvedFees.total_discount > 0 && (
                        <Typography component="span" variant="caption" sx={{ mr: 1, color: '#DC2626' }}>
                          (خصم {resolvedFees.total_discount} ₪)
                        </Typography>
                      )}
                    </Typography>
                    <Typography fontWeight={800} sx={{ color: '#4338CA', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{totalSchoolCost} ₪</Typography>
                  </Box>
                </Box>
              </Box>
            ) : !feesLoading && !hasLegacyFeeData ? (
              /* ── No fees configured for this student ── */
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography sx={{ color: '#6B7280', mb: 2, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                  لا توجد رسوم مسجلة لهذا الطالب
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={openAddCategoryDialog}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    borderColor: '#4338CA',
                    color: '#4338CA',
                    '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
                  }}
                >
                  إضافة رسوم
                </Button>
              </Box>
            ) : (
              /* ── Legacy fields fallback ── */
              !feesLoading && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="القسط السنوي" type="number"
                      value={feeInputs.annualFee}
                      onChange={(e) => handleFeeChange('annualFee', e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="المواصلات" type="number"
                      value={feeInputs.transportFee}
                      onChange={(e) => handleFeeChange('transportFee', e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="كتب" type="number"
                      value={feeInputs.booksFee}
                      onChange={(e) => handleFeeChange('booksFee', e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }} />
                  </Grid>
                  {feeInputs.previousDebt > 0 && (
                    <Grid item xs={12}>
                      <Chip
                        label={`دين سابق مرحّل: ${feeInputs.previousDebt.toFixed(2)} ₪`}
                        color="warning"
                        sx={{ fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
                      <FormControlLabel
                        control={
                          <Checkbox checked={feeInputs.clothesFeePaid}
                            onChange={(e) => handleFeeChange('clothesFeePaid', e.target.checked)}
                            color="primary" />
                        }
                        label={
                          <Typography fontWeight="600"
                            color={feeInputs.clothesFeePaid ? 'success.main' : 'warning.main'}>
                            تم دفع رسوم الملابس
                          </Typography>
                        }
                        sx={{
                          backgroundColor: feeInputs.clothesFeePaid ? '#e8f5e8' : '#fff3e0',
                          borderRadius: 2, px: 2, py: 0.5,
                          border: feeInputs.clothesFeePaid ? '2px solid #4caf50' : '2px solid #ff9800',
                        }}
                      />
                      <Button variant="contained" onClick={handleSaveFee}
                        disabled={!selectedStudent || isSaving}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 700,
                          textTransform: 'none',
                          px: 3,
                          fontFamily: 'Cairo, Tajawal, sans-serif',
                          bgcolor: '#4338CA',
                          boxShadow: 'none',
                          '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                        }}>
                        {isSaving ? 'جاري الحفظ...' : 'حفظ الرسوم'}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              )
            )}
          </Box>

          {/* Summary Section — hidden while the "no fees configured" message is showing */}
          {(resolvedFees?.fee_items?.length > 0 || hasLegacyFeeData) && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: '#fff',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: '#E0F2FE',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <AttachMoneyIcon sx={{ color: '#0284C7', fontSize: 26 }} />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    المبلغ الإجمالي للمدرسة
                  </Typography>
                  <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    {totalSchoolCost} شيكل
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: '#fff',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: '#FEF3C7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CreditScoreIcon sx={{ color: '#D97706', fontSize: 26 }} />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    المبلغ المتبقي
                  </Typography>
                  <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#D97706', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    {remainingAmount} شيكل
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          )}
        </Paper>

        {validationErrors.global && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
            {validationErrors.global}
          </Alert>
        )}

        
        {payments.map((payment, index) => {
          const isReceived = payment.receivedFromAPI && payment.received === payment.receivedFromAPI && payment.isExisting;
          const isLocked = false;
          const isCheque = payment.type === 'شيك';
          const isVisa = payment.type === 'فيزا'; // ✅ ADD THIS
          
          return (
            <MotionPaper
              key={index}
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 3,
                bgcolor: '#fff',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                border: '1px solid #E5E7EB',
              }}
            >
              <Stack useFlexGap direction="row" alignItems="center" spacing={1.5} mb={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: isReceived ? '#DCFCE7' : '#EEF2FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <AttachMoneyIcon sx={{ color: isReceived ? '#16A34A' : '#4338CA', fontSize: 24 }} />
                </Box>
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                >
                  دفعة رقم {index + 1}
                </Typography>
                {isReceived && (
                  <Chip
                    label="تم التحصيل"
                    size="small"
                    sx={{
                      bgcolor: '#DCFCE7',
                      color: '#16A34A',
                      fontWeight: 700,
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                    }}
                  />
                )}
                {payment.isExisting && payment.created_by_name && (
                  <Chip
                    icon={<AdminPanelSettingsIcon sx={{ fontSize: 16, color: '#0284C7 !important' }} />}
                    label={`منشئ السند: ${payment.created_by_name}`}
                    size="small"
                    sx={{
                      bgcolor: '#E0F2FE',
                      color: '#0284C7',
                      fontWeight: 700,
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                    }}
                  />
                )}
                <Box sx={{ ml: 'auto' }}>
                  <IconButton
                    size="small"
                    onClick={() => handleDeletePaymentClick(index)}
                    title="حذف الدفعة"
                    sx={{
                      color: '#DC2626',
                      bgcolor: '#FEE2E2',
                      '&:hover': { bgcolor: '#FECACA' },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Stack>
              
              <Grid container spacing={2} alignItems="center">
                {/* Amount Field */}
                <Grid item xs={12} md={2.5}>
                  <TextField
                    fullWidth
                    label="المبلغ"
                    value={payment.amount}
                    onChange={(e) => handleChange(index, 'amount', e.target.value)}
                    error={!!validationErrors[`payment_${index}_amount`]}
                    helperText={validationErrors[`payment_${index}_amount`]}
                    type="number"
                    disabled={isLocked}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                  />
                </Grid>

                {/* Payment Type Field */}
                <Grid item xs={12} md={3}>
                  <TextField
                    label="طريقة الدفع"
                    name="type"
                    value={payment.paymentTypeId || ''}
                    onChange={(e) => {
                      const selectedType = paymentTypes.find((pt) => pt.id === e.target.value);
                      handleChange(index, 'type', selectedType?.display_name || '');
                      handleChange(index, 'paymentTypeId', e.target.value);
                    }}
                    select
                    fullWidth
                    disabled={isLocked}
                    error={!!validationErrors[`payment_${index}_type`]}
                    helperText={validationErrors[`payment_${index}_type`]}
                    sx={{
                      minWidth: '150px',
                      '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' },
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                    }}
                    InputProps={{
                      style: { fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }
                    }}
                  >
                    {paymentTypes.map((pt) => (
                      <MenuItem key={pt.id} value={pt.id} style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                        {pt.display_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Date Field */}
                <Grid item xs={12} md={2.5}>
                  <TextField
                    fullWidth
                    label="تاريخ الدفع"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={payment.date}
                    onChange={(e) => handleChange(index, 'date', e.target.value)}
                    error={!!validationErrors[`payment_${index}_date`]}
                    helperText={validationErrors[`payment_${index}_date`]}
                    disabled={isLocked}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                  />
                </Grid>

                {/* Received Checkbox */}
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={payment.received}
                        onChange={(e) => handleChange(index, 'received', e.target.checked)}
                        disabled={isLocked}
                      />
                    }
                    label="تم التحصيل"
                  />
                </Grid>
                
                {/* ✅ VISA DETAILS - show only for Visa payments */}
                {isVisa && (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="رقم إيصال الفيزا"
                        value={payment.visaReceiptNumber || ''}
                        onChange={(e) => handleChange(index, 'visaReceiptNumber', e.target.value)}
                        disabled={isLocked}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                        placeholder="أدخل رقم إيصال الفيزا"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="ملاحظات الفيزا"
                        value={payment.note || ''}
                        onChange={(e) => handleChange(index, 'note', e.target.value)}
                        disabled={isLocked}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                        placeholder="ملاحظات إضافية للدفع بالفيزا"
                      />
                    </Grid>
                  </>
                )}

                {/* CHEQUE DETAILS - show only for cheques */}
                {isCheque && (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="تاريخ استحقاق الشيك"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={payment.chequeDueDate || ''}
                        onChange={(e) => handleChange(index, 'chequeDueDate', e.target.value)}
                        error={!!validationErrors[`payment_${index}_chequeDueDate`]}
                        helperText={validationErrors[`payment_${index}_chequeDueDate`]}
                        disabled={isLocked}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                      />
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <BankBranchSelect
                        fieldSx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                        bankId={payment.bankId}
                        bankName={payment.bankName}
                        branchId={payment.branchId}
                        branchName={payment.branchName}
                        disabled={isLocked}
                        onChange={(patch) => {
                          const updated = [...payments];
                          updated[index] = { ...updated[index], ...patch };
                          setPayments(updated);
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="رقم الحساب"
                        value={payment.accountNumber || ''}
                        onChange={(e) => handleChange(index, 'accountNumber', e.target.value)}
                        disabled={isLocked}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="رقم الشيك"
                        value={payment.chequeNumber || ''}
                        onChange={(e) => handleChange(index, 'chequeNumber', e.target.value)}
                        error={!!validationErrors[`payment_${index}_chequeNumber`]}
                        helperText={validationErrors[`payment_${index}_chequeNumber`]}
                        disabled={isLocked}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                      />
                    </Grid>

                    {/* Image Upload/View Section for Cheques */}
                    <Grid item xs={12} md={6}>
                      {!payment.chequeImagePreview ? (
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          disabled={isLocked}
                          startIcon={<CloudUploadIcon />}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 700,
                            textTransform: 'none',
                            fontFamily: 'Cairo, Tajawal, sans-serif',
                            borderColor: '#4338CA',
                            color: '#4338CA',
                            bgcolor: '#F8F9FF',
                            '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
                          }}
                        >
                          تحميل صورة الشيك
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => handleFileChange(index, e.target.files[0])}
                            disabled={isLocked}
                          />
                        </Button>
                      ) : (
                        <Stack useFlexGap direction="row" spacing={1} alignItems="center">
                          <IconButton
                            onClick={() => handleViewImage(payment.chequeImagePreview)}
                            disabled={isLocked}
                            size="small"
                            sx={{ color: '#4338CA', bgcolor: '#EEF2FF', '&:hover': { bgcolor: '#E0E7FF' } }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="body2" sx={{ flex: 1, color: '#1E1B4B', fontWeight: 600, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                            صورة الشيك
                          </Typography>
                          <IconButton
                            onClick={() => handleRemoveImage(index)}
                            disabled={isLocked}
                            size="small"
                            sx={{ color: '#DC2626', bgcolor: '#FEE2E2', '&:hover': { bgcolor: '#FECACA' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      )}
                    </Grid>
                                            
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="ملاحظات"
                        value={payment.description !== '' ?  payment.description : payment.note}
                        onChange={(e) => handleChange(index, 'note', e.target.value)}
                        disabled={isLocked}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                      />
                    </Grid>
                  </>
                )}

                {/* For Cash payments - only show notes field */}
                {!isCheque && !isVisa && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="ملاحظات"
                      value={payment.note || ''}
                      onChange={(e) => handleChange(index, 'note', e.target.value)}
                      disabled={isLocked}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8F9FF' }, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                      placeholder="ملاحظات إضافية"
                    />
                  </Grid>
                )}
              </Grid>
              
              {/* Payment summary */}
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mt: 1,
                    bgcolor: '#EEF2FF',
                    color: '#4338CA',
                    borderRadius: 2,
                    px: 2.5,
                    py: 1,
                    fontWeight: 700,
                    display: 'inline-block',
                    minWidth: 180,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}
                >
                  الباقي: {Math.max(Number(totalSchoolCost) - payments.reduce((sum, p, i) => i <= index ? sum + Number(p.amount || 0) : sum, 0), 0)} شيكل
                </Typography>
              </Box>
            </MotionPaper>
          );
        })}

        <Divider sx={{ my: 4, borderColor: '#E5E7EB' }} />

        <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
              color: '#fff',
              borderRadius: 3,
              px: 3,
              py: 1.2,
              fontWeight: 800,
              fontFamily: 'Cairo, Tajawal, sans-serif',
            }}
          >
            المجموع المدفوع: {totalCollected} شيكل
          </Typography>
          <Typography
            variant="h6"
            sx={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
              color: '#fff',
              borderRadius: 3,
              px: 3,
              py: 1.2,
              fontWeight: 800,
              fontFamily: 'Cairo, Tajawal, sans-serif',
            }}
          >
            المجموع المجدول غير محصل: {totalUncollected} شيكل
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="center" mt={2} flexWrap="wrap" useFlexGap>
          {/* WhatsApp Button for Parent Reminder */}
          <Button
  variant="contained"
  color="success"
  startIcon={<WhatsAppIcon sx={{ mr: 1.2 }} />}
  disabled={!selectedStudent}
  onClick={() => {
    if (!selectedStudent) return;
    
    // Format phone number correctly
    const phoneCode = selectedStudent.parent_phone_code || '970';
    const phoneNumber = selectedStudent.parent_phone || '';
    
    // Remove leading zero if exists
    const cleanPhone = phoneNumber.startsWith('0') 
      ? phoneNumber.substring(1) 
      : phoneNumber;
    
    const formattedPhone = `+${phoneCode}${cleanPhone}`;
    
    if (!cleanPhone) {
      alert('لا يوجد رقم هاتف صحيح لولي الأمر');
      return;
    }
    
    
    // Create WhatsApp message
    const msg =
      `تذكير من المدرسة:\n` +
      `اسم الطالب: ${selectedStudent.first_name || ''} ${selectedStudent.second_name || ''}\n` +
       `المبلغ الإجمالي المطلوب: ${totalSchoolCost} شيكل\n` +
                `المبلغ المتبقي: ${remainingAmount} شيكل\n`;
    
    const url = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  }}
  sx={{
    borderRadius: 2.5,
    fontWeight: 700,
    textTransform: 'none',
    px: 2.5,
    minWidth: 160,
    fontFamily: 'Cairo, Tajawal, sans-serif',
    boxShadow: 'none',
    backgroundColor: '#25D366',
    '&:hover': { backgroundColor: '#1ebe5d', boxShadow: 'none' },
  }}
>
  تذكير ولي الأمر واتساب
</Button>
          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            disabled={isOverpaid}
            onClick={addPaymentRow}
            sx={{
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              px: 2.5,
              minWidth: 160,
              fontFamily: 'Cairo, Tajawal, sans-serif',
              borderColor: '#4338CA',
              color: '#4338CA',
              '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
              opacity: isOverpaid ? 0.5 : 1
            }}
          >
            إضافة دفعة جديدة
          </Button>
          <Button
            variant="contained"
            onClick={handleFinish}
            disabled={isSaving || isOverpaid || Object.keys(validationErrors).length > 0}
            sx={{
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              px: 3,
              minWidth: 160,
              fontFamily: 'Cairo, Tajawal, sans-serif',
              bgcolor: '#4338CA',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
              opacity: (isSaving || isOverpaid || Object.keys(validationErrors).length > 0) ? 0.5 : 1
            }}
          >
            {isSaving ? 'جاري الحفظ...' : 'إنهاء'}
          </Button>
        </Stack>
        </Box>
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDeletePayment}
        PaperProps={{ sx: { borderRadius: 3, direction: 'rtl', minWidth: { xs: 280, sm: 380 } } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
          تأكيد حذف الدفعة
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
            هل أنت متأكد من حذف هذه الدفعة؟ لا يمكن التراجع عن هذا الإجراء.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCancelDeletePayment}
            variant="outlined"
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              borderColor: '#E5E7EB',
              color: '#6B7280',
              '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F8F9FF' },
            }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleConfirmDeletePayment}
            variant="contained"
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              bgcolor: '#DC2626',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' },
            }}
          >
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addCategoryOpen}
        onClose={closeAddCategoryDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, fontFamily: 'Cairo, Tajawal, sans-serif' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif', borderBottom: '3px solid', borderImage: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%) 1' }}>
          إضافة نوع رسوم جديد
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="اسم الرسوم"
            value={categoryNameInput}
            onChange={(e) => { setCategoryNameInput(e.target.value); setCategoryNameError(''); }}
            error={!!categoryNameError}
            helperText={categoryNameError || 'مثال: رسوم الدراسة، رسوم الكتب، رسوم المختبر'}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeAddCategoryDialog}
            variant="outlined"
            sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif', borderColor: '#E5E7EB', color: '#6B7280' }}
          >
            إلغاء
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCategory}
            sx={{
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 700,
              fontFamily: 'Cairo, Tajawal, sans-serif',
              px: 3,
              bgcolor: '#4338CA',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
            }}
          >
            حفظ
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default StudentPaymentPage;